import { useState, useEffect, useCallback } from 'react';
import { getIntegrations, type Integration } from '@/app/services/common/integrations-service';

interface UseIntegrationsResult {
  integrations: Integration[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch available integrations
 * 
 * @returns Object containing integrations data, loading state, error state, and refetch function
 */
export function useIntegrations(): UseIntegrationsResult {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const refetch = useCallback(() => {
    setRefetchTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadIntegrations = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await getIntegrations();

        if (!cancelled) {
          setIntegrations(data);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to fetch integrations'));
          setIsLoading(false);
        }
      }
    };

    loadIntegrations();

    return () => {
      cancelled = true;
    };
  }, [refetchTrigger]);

  return {
    integrations,
    isLoading,
    error,
    refetch
  };
}
